import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Валидатор для проверки корректности Telegram логина/ссылки
 * Поддерживает форматы:
 * - @username
 * - username
 * - https://t.me/username
 * - https://telegram.me/username
 * - http://t.me/username
 * - http://telegram.me/username
 */
export function telegramLoginValidator(): ValidatorFn {
  // Telegram username должен содержать только латинские буквы, цифры и подчеркивания
  // Длина: от 5 до 32 символов (актуальные требования Telegram)
  const USERNAME_REGEX = /^[a-zA-Z0-9_]{5,32}$/;

  // Допустимые домены для Telegram ссылок
  const ALLOWED_DOMAINS = ['t.me', 'telegram.me'];

  // Ключ ошибки валидации
  const ERROR_KEY = 'telegramLogin';
  const ERROR_MESSAGE = 'form-errors.telegram-format';

  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue: string = control.value;

    // Пустое значение считаем валидным (используйте Validators.required отдельно)
    if (!rawValue) {
      return null;
    }

    const value = String(rawValue).trim();

    // Пустая строка после trim
    if (!value) {
      return null;
    }

    try {
      // Проверяем URL формат
      if (isUrl(value)) {
        return validateTelegramUrl(
          value,
          USERNAME_REGEX,
          ALLOWED_DOMAINS,
          ERROR_KEY,
          ERROR_MESSAGE,
        );
      }

      // Проверяем username формат (@username или username)
      return validateTelegramUsername(value, USERNAME_REGEX, ERROR_KEY, ERROR_MESSAGE);
    } catch (error) {
      console.error(error);
      // Любая неожиданная ошибка считается невалидным форматом
      return { [ERROR_KEY]: ERROR_MESSAGE };
    }
  };
}

/**
 * Проверяет, является ли строка URL (с протоколом или без)
 */
function isUrl(value: string): boolean {
  return (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.includes('t.me/') ||
    value.includes('telegram.me/')
  );
}

/**
 * Валидирует Telegram URL
 */
function validateTelegramUrl(
  value: string,
  usernameRegex: RegExp,
  allowedDomains: string[],
  errorKey: string,
  errorMessage: string,
): ValidationErrors | null {
  try {
    const url = new URL(value);

    // Проверяем домен
    const isDomainValid = allowedDomains.some(
      (domain) => url.hostname.toLowerCase() === domain.toLowerCase(),
    );

    if (!isDomainValid) {
      return { [errorKey]: errorMessage };
    }

    // Извлекаем username из пути (убираем ведущие слеши)
    const username = url.pathname.replace(/^\/+/, '').replace(/\/+$/, '');

    // Проверяем username
    if (!username || !usernameRegex.test(username)) {
      return { [errorKey]: errorMessage };
    }

    return null;
  } catch {
    // Невалидный URL
    return { [errorKey]: errorMessage };
  }
}

/**
 * Валидирует Telegram username (с @ или без)
 */
function validateTelegramUsername(
  value: string,
  usernameRegex: RegExp,
  errorKey: string,
  errorMessage: string,
): ValidationErrors | null {
  // Убираем @ если есть
  const username = value.startsWith('@') ? value.slice(1) : value;

  // Проверяем username по регулярному выражению
  if (!usernameRegex.test(username)) {
    return { [errorKey]: errorMessage };
  }

  return null;
}

// Пример использования в компоненте:
/*
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { telegramLoginValidator } from './telegram-login.validator';

@Component({
  selector: 'app-user-form',
  template: `
    <form [formGroup]="userForm">
      <input
        type="text"
        formControlName="telegramLogin"
        placeholder="@username, username, t.me/username или https://t.me/username"
      >
      <div *ngIf="userForm.get('telegramLogin')?.errors?.['telegramLogin']">
        {{ userForm.get('telegramLogin')?.errors?.['telegramLogin'] | translate }}
      </div>
    </form>
  `
})
export class UserFormComponent {
  userForm: FormGroup;

  constructor(private fb: FormBuilder) {
    this.userForm = this.fb.group({
      telegramLogin: ['', [Validators.required, telegramLoginValidator()]]
    });
  }
}
*/
